/**
 * ShopSlotPreviewModal — admins tap "Preview" on any of the 8 sample-photo
 * slots to see how the uploaded image will render inside a real /shop
 * product card BEFORE they publish. Renders a full `PremiumProductCard`
 * with a mock product so the crop, aspect ratio and hover polish are
 * exactly what shoppers will see.
 */
import React from "react";
import { X as XIcon } from "lucide-react";
import { PremiumProductCard } from "@/components/shop/ProductCards";
import type { Product } from "@/lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
  slotIndex: number;
  /** The uploaded data-URL, OR null for the shipped default fallback. */
  imageUrl: string | null;
  /** Shipped default URL (already resolved to the correct Picsum photo). */
  fallbackUrl: string;
}

// Deterministic mock product — same look for every slot so admins can
// tell photo edits apart without other card content shifting around.
const MOCK: Omit<Product, "image_url" | "product_id" | "slug"> & { product_id: string; slug: string } = {
  product_id: "preview-mock",
  slug: "preview-mock",
  title: "Sample Product · Preview only",
  brand: "SociBaba",
  price: 2499,
  compare_at_price: 3499,
  currency: "USD" as any,
  category: "Preview",
  product_score: 812,
  verified_brand: true,
} as any;

export const ShopSlotPreviewModal: React.FC<Props> = ({ open, onClose, slotIndex, imageUrl, fallbackUrl }) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const mock: Product = {
    ...(MOCK as any),
    image_url: imageUrl || fallbackUrl,
    slug: `preview-slot-${slotIndex}`,
    product_id: `preview-slot-${slotIndex}`,
    title: `Sample Product · Slot ${slotIndex + 1} preview`,
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview slot ${slotIndex + 1}`}
      data-testid="shop-slot-preview-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
    >
      <button
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        data-testid="shop-slot-preview-backdrop"
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl rounded-2xl p-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="shop-slot-preview-close"
          className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center rounded-full bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
        >
          <XIcon className="h-4 w-4" />
        </button>
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">
            Preview · Slot {slotIndex + 1}
          </div>
          <div className="text-lg font-display font-bold text-slate-900 dark:text-white mt-0.5">
            How it looks on /shop
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {imageUrl
              ? "Your uploaded photo, rendered inside a real product card. Publish it as-is or replace it if the crop looks off."
              : "Shipped default photo — every empty slot falls back to this. Upload your own to replace it."}
          </div>
        </div>
        <div className="max-w-[280px] mx-auto">
          <PremiumProductCard p={mock as any} listName="slot-preview" index={slotIndex} />
        </div>
      </div>
    </div>
  );
};

export default ShopSlotPreviewModal;
